import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { getPaymentEnvironment } from "@/lib/paddle";

export type UnlockTier = "matches" | "blueprint" | "accelerator";

interface UnlockState {
  matches: boolean;
  blueprint: boolean;
  accelerator: boolean;
  loading: boolean;
  refresh: () => Promise<void>;
}

export function useUnlocks(): UnlockState {
  const { user } = useAuth();
  const [matches, setMatches] = useState(false);
  const [blueprint, setBlueprint] = useState(false);
  const [accelerator, setAccelerator] = useState(false);
  const [loading, setLoading] = useState(true);

  const env = getPaymentEnvironment();

  const refresh = useCallback(async () => {
    if (!user) {
      setMatches(false);
      setBlueprint(false);
      setAccelerator(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    const { data, error } = await supabase
      .from("unlocks")
      .select("tier, unlocked, revoked_at")
      .eq("user_id", user.id)
      .eq("environment", env);

    if (error) {
      console.error("Failed to load unlocks:", error);
      setLoading(false);
      return;
    }

    const active = (data ?? []).filter((u) => u.unlocked && !u.revoked_at);
    setMatches(active.some((u) => u.tier === "matches"));
    setBlueprint(active.some((u) => u.tier === "blueprint"));
    setAccelerator(active.some((u) => u.tier === "accelerator"));
    setLoading(false);
  }, [user, env]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Realtime: refresh when an unlock row changes
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`unlocks-${user.id}-${Math.random().toString(36).slice(2, 8)}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "unlocks",
          filter: `user_id=eq.${user.id}`,
        },
        () => refresh()
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, refresh]);

  return { matches, blueprint, accelerator, loading, refresh };
}
